import React, { useTransition } from "react";
import { useDispatch } from "react-redux";
import { closeModal, setModalType } from "../../redux/slices/uiSlice";
import ModalWrapper from "./ModalWrapper";
import { forgotPasswordAPI } from "../../api/auth";
import useToast from "../../hooks/useToast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { REGEX } from "../../utils/constant";
import BlueBtn from "../button/PrimaryBtn";
import { useTranslation } from "react-i18next";

const ForgotPasswordModal: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleBack = () => {
    dispatch(setModalType("login"));
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().matches(REGEX.EMAIL, t("validation.emailValid")).required(t("validation.emailRequired")),
    }),
    onSubmit: (values) => {
      mutate(values.email as string);
    },
  });

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: forgotPasswordAPI,
    onSuccess: (data) => {
      useToast(t("messages.resetLinkSent"), "success");
      dispatch(setModalType("resetLinkSent"));
      dispatch(closeModal());
    },
    onError: (error: any) => {
      useToast(error.message || t("messages.failedToSendLink"), "error");
    },
  });

  return (
    <ModalWrapper onClose={handleClose} showBackButton onBack={handleBack}>
      {/* Header */}
      <div className="text-center lg:mb-[1.2vw] mb-4">
        <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
          {t("forgotPassword.forgotPassword")}
        </h1>
        <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
          {t("forgotPassword.enterEmail")}
        </p>
      </div>

      {/* Email Input */}
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
  `}>
          <input
            type="text"
            name="email"
            placeholder={t("forgotPassword.emailAddress")}
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
          />
        </div>
        {errors.email && touched.email && (
          <p className="text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
            {errors.email}
          </p>
        )}
      </div>

      {/* Send Reset Link Button */}
      <button
        type="button"
        disabled={isPending}
        onClick={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
      >
        <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block">{isPending ? t("forgotPassword.sending") : t("forgotPassword.getResetLink")}</span></span>
      </button>
    </ModalWrapper>
  );
};

export default ForgotPasswordModal;
